/**
 * Lyrics Panel
 * Handles lyrics panel UI and settings
 */

import { lyricsState, playlistState, managers } from './state.js';
import { elements } from './dom-cache.js';
import { extractLyrics } from './file-handler.js';
import { updatePlaylistUI, openLyricsPanel, closeLyricsPanel, clearTrackLyrics } from './playlist.js';
import { recalculateLyricsScrollParams } from './visualizer-controls.js';

/**
 * Set up lyrics panel event listeners
 */
export function setupLyricsPanelListeners() {
  // Helper to safely add listener
  const addListener = (id, event, handler) => {
    const el = document.getElementById(id);
    if (el) el.addEventListener(event, handler);
  };

  // Close button
  addListener('closeLyricsPanel', 'click', closeLyricsPanel);

  // Swap lyrics button
  addListener('swapLyricsBtn', 'click', () => {
    if (elements.lyricsFileInput) elements.lyricsFileInput.click();
  });

  // Clear lyrics button
  addListener('clearLyricsBtn', 'click', clearTrackLyrics);

  // Lyrics file input handler
  if (elements.lyricsFileInput) {
    elements.lyricsFileInput.addEventListener('change', async (e) => {
      const file = e.target.files[0];
      if (file && lyricsState.lyricsPanelTrackIndex >= 0) {
        const lyrics = await extractLyrics(file);
        const track = playlistState.playlist[lyricsState.lyricsPanelTrackIndex];
        track.lyrics = lyrics;
        track.lyricsFile = file;
        // Clear aligned lyrics since uploaded lyrics don't have timing data
        track.alignedLyrics = null;
        updatePlaylistUI();

        // Recalculate scroll params if this is current track
        if (lyricsState.lyricsPanelTrackIndex === playlistState.currentTrackIndex && lyrics) {
          if (track.duration && elements.lyricsOverlayCanvas) {
            lyricsState.currentLyricsScrollParams = managers.lyricsRenderer.calculateScrollParams(
              lyrics,
              track.duration,
              elements.lyricsOverlayCanvas.width,
              elements.lyricsOverlayCanvas.height
              // No alignedLyrics since user uploaded lyrics don't have timing
            );
          }
        }

        if (lyrics) {
          openLyricsPanel(lyricsState.lyricsPanelTrackIndex);
        }
      }
      e.target.value = '';
    });
  }
}

/**
 * Set up lyrics settings event listeners
 */
export function setupLyricsSettingsListeners() {
  // Helper to safely add listener
  const addListener = (id, event, handler) => {
    const el = document.getElementById(id);
    if (el) el.addEventListener(event, handler);
  };

  // Note: Lyrics accordion toggle is handled by settings-overlay.js setupAccordions()
  // Do not add duplicate handlers here as it causes double-toggling

  // Lyrics enabled checkbox
  addListener('lyricsEnabled', 'change', (e) => {
    lyricsState.lyricsEnabled = e.target.checked;
  });

  // Font size slider
  addListener('lyricsFontSize', 'input', (e) => {
    const value = parseInt(e.target.value);
    const valueEl = document.getElementById('lyricsFontSizeValue');
    if (valueEl) valueEl.textContent = value;
    managers.lyricsRenderer.setConfig({ fontSize: value });
    recalculateLyricsScrollParams();
  });

  // Color picker
  addListener('lyricsColor', 'input', (e) => {
    managers.lyricsRenderer.setConfig({ color: e.target.value });
  });

  // Scroll start position
  addListener('lyricsScrollStart', 'input', (e) => {
    const value = parseInt(e.target.value);
    const valueEl = document.getElementById('lyricsScrollStartValue');
    if (valueEl) valueEl.textContent = value;
    managers.lyricsRenderer.setConfig({ scrollStartY: value / 100 });
    recalculateLyricsScrollParams();
  });

  // Scroll end position
  addListener('lyricsScrollEnd', 'input', (e) => {
    const value = parseInt(e.target.value);
    const valueEl = document.getElementById('lyricsScrollEndValue');
    if (valueEl) valueEl.textContent = value;
    managers.lyricsRenderer.setConfig({ scrollEndY: value / 100 });
    recalculateLyricsScrollParams();
  });
}

/**
 * Set up performance monitor event listeners
 */
export function setupPerformanceMonitorListeners() {
  // Helper to safely add listener
  const addListener = (id, event, handler) => {
    const el = document.getElementById(id);
    if (el) el.addEventListener(event, handler);
  };

  // Note: Performance accordion toggle is handled by settings-overlay.js setupAccordions()
  // Do not add duplicate handlers here as it causes double-toggling

  // Performance monitor enabled checkbox
  addListener('perfMonitorEnabled', 'change', (e) => {
    if (e.target.checked) {
      managers.perfMonitor.start();
    } else {
      managers.perfMonitor.stop();
    }
  });
}
